import React, { useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const ViewTypeBreakdownChartJS = ({ data = {}, rawViewsData, processedData }) => {
    const chartRef = useRef();

    // COMPREHENSIVE DEBUGGING
    console.group('🔍 ViewTypeBreakdownChartJS - COMPREHENSIVE DEBUG');
    console.log('📊 Props received:', { data, rawViewsData, processedData });
    console.log('📊 data type:', typeof data);
    console.log('📊 data keys:', Object.keys(data || {}));
    console.log('📊 data.byType exists:', !!data.byType);
    console.log('📊 direct data values:', {
        accessory: data.accessory,
        qr_code: data.qr_code,
        share_link: data.share_link
    });
    console.log('📊 byType data values:', {
        accessory: data.byType?.accessory,
        qr_code: data.byType?.qr_code,
        share_link: data.byType?.share_link
    });
    console.groupEnd();    // Normalize input data to ensure all required keys exist
    const normalizedData = useMemo(() => {
        console.group('🔧 ViewTypeBreakdownChartJS Data Normalization');
        console.log('📊 Input data for normalization:', data);

        let result = {
            accessory: 0,
            qr_code: 0,
            share_link: 0
        };

        // Handle different data structures
        if (data) {
            if (typeof data === 'object') {
                if (data.byType) {
                    // Structure: { byType: { accessory: 0, qr_code: 5, share_link: 0 } }
                    console.log('📊 Using byType structure');
                    result.accessory = Number(data.byType.accessory) || 0;
                    result.qr_code = Number(data.byType.qr_code) || 0;
                    result.share_link = Number(data.byType.share_link) || 0;
                } else if ('accessory' in data || 'qr_code' in data || 'share_link' in data) {
                    // Structure: { accessory: 0, qr_code: 5, share_link: 0 }
                    console.log('📊 Using direct property structure');
                    result.accessory = Number(data.accessory) || 0;
                    result.qr_code = Number(data.qr_code) || 0;
                    result.share_link = Number(data.share_link) || 0;
                } else if (processedData && processedData.byType) {
                    // Try using processedData if available
                    console.log('📊 Using processedData.byType structure');
                    result.accessory = Number(processedData.byType.accessory) || 0;
                    result.qr_code = Number(processedData.byType.qr_code) || 0;
                    result.share_link = Number(processedData.byType.share_link) || 0;
                }
            }
        }

        console.log('📊 FINAL normalized data:', result);
        console.groupEnd();
        return result;
    }, [data, processedData]);// Calculate total for percentage calculations
    const totalViews = useMemo(() => {
        const total = Object.values(normalizedData).reduce((sum, val) => sum + val, 0);
        console.log('Total views calculated:', total);
        return total;
    }, [normalizedData]);

    // Prepare data for display
    const breakdownData = [
        {
            name: 'Accessory',
            value: normalizedData.accessory,
            percentage: totalViews > 0 ? ((normalizedData.accessory / totalViews) * 100).toFixed(1) : '0.0',
            color: '#3ABFF5'
        },
        {
            name: 'QR Code',
            value: normalizedData.qr_code,
            percentage: totalViews > 0 ? ((normalizedData.qr_code / totalViews) * 100).toFixed(1) : '0.0',
            color: '#8CF1D9'
        },
        {
            name: 'Share',
            value: normalizedData.share_link,
            percentage: totalViews > 0 ? ((normalizedData.share_link / totalViews) * 100).toFixed(1) : '0.0',
            color: '#EEA5F5'
        }
    ]; console.log('Breakdown data prepared:', breakdownData); console.log('About to render legend with values:', breakdownData.map(item => `${item.name}: ${item.value}`));
    console.log('Each item render check:', breakdownData.map(item => ({
        name: item.name,
        value: item.value,
        displayText: `${item.name} (${item.value})`
    })));

    // Filter out zero values for cleaner chart
    const filteredData = breakdownData.filter(item => item.value > 0);
    const hasData = filteredData.length > 0;

    // Chart.js data configuration
    const chartData = {
        labels: hasData ? filteredData.map(item => item.name) : ['No Data'],
        datasets: [
            {
                data: hasData ? filteredData.map(item => item.value) : [1],
                backgroundColor: hasData ? filteredData.map(item => item.color) : ['#E0E0E0'],
                borderColor: hasData ? filteredData.map(item => item.color) : ['#E0E0E0'], borderWidth: 2,
                hoverOffset: 4
            }
        ]
    };

    // Chart.js options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#333333',
                bodyColor: '#666666',
                borderColor: '#E0E0E0',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                titleFont: {
                    size: 12,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 11
                },
                padding: 12,
                callbacks: {
                    label: function (context) {
                        if (!hasData) return 'No data available';
                        const item = filteredData[context.dataIndex];
                        return `${item.name}: ${item.value} (${item.percentage}%)`;
                    }
                }
            }
        },
        animation: {
            duration: 750,
            easing: 'easeInOutQuart'
        },
        onClick: (event, elements) => {
            if (elements.length > 0 && hasData) {
                const elementIndex = elements[0].index;
                const clickedItem = filteredData[elementIndex];
                console.log(`Clicked on ${clickedItem.name}: ${clickedItem.value} views`);
            }
        }
    };

    return (
        <Box
            sx={{
                height: '300px',
                backgroundColor: '#ffffff',
                borderRadius: '18px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header */}            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    marginBottom: '20px'
                }}
            >                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#333333' }}>
                    View Type Distribution (Debug: Total = {totalViews})
                </Typography>
                <Tooltip title="Distribution of views by type with interactive pie chart">
                    <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                </Tooltip>
            </Box>

            {hasData ? (
                <>
                    {/* Chart */}
                    <Box
                        sx={{
                            height: '140px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}
                    >
                        <Pie ref={chartRef} data={chartData} options={options} />
                    </Box>                    {/* Legend */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {breakdownData.map((item, index) => {
                            // Add debugging for each render
                            console.log(`Rendering legend item ${index}:`, {
                                name: item.name,
                                value: item.value,
                                displayText: `${item.name} (${item.value})`
                            });

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        opacity: item.value > 0 ? 1 : 0.5
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Box
                                            sx={{
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: item.color,
                                                borderRadius: '50%'
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                color: '#333333'
                                            }}
                                        >
                                            {item.name} ({item.value})
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#888888', fontWeight: 500 }}>
                                            {item.percentage}%
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#888888'
                    }}
                >
                    <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                        No view data available
                    </Typography>
                    <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                        View data will appear here once you start receiving profile views
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ViewTypeBreakdownChartJS;
